import { PortalTooltip } from "@/components/ui/PortalTooltip";

interface EmailConfirmationViewProps {
  isConfirmed: boolean;
  email?: string;
  redirectBackToLogin: () => Promise<void>;
  onResendEmail: () => Promise<void>;
  isResending?: boolean;
}

export const EmailConfirmationView = ({
  isConfirmed,
  email,
  redirectBackToLogin,
  onResendEmail,
  isResending = false,
}: EmailConfirmationViewProps) => {
  return (
    <div className="text-center">
      <div className="mb-8 flex justify-center">
        <div
          className={`rounded-full p-4 ${isConfirmed ? "bg-success-subtle text-success-text" : "bg-success-subtle text-success-text"}`}
        >
          {isConfirmed ? (
            <svg className="h-12 w-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M5 13l4 4L19 7"
              />
            </svg>
          ) : (
            <svg className="h-12 w-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
              />
            </svg>
          )}
        </div>
      </div>

      <h2 className="mb-4 text-3xl font-bold text-foreground">
        {isConfirmed ? "Email Confirmed!" : "Check Your Email"}
      </h2>

      {isConfirmed ? (
        <div className="space-y-6">
          <p className="mx-auto max-w-sm leading-relaxed text-muted-foreground">
            Great news! Your email address has been successfully verified. Redirecting you to
            Paribus AI now...
          </p>
          <div className="flex justify-center">
            <div className="h-1.5 w-32 overflow-hidden rounded-full bg-surface-sunken">
              <div className="animate-progress h-full bg-primary"></div>
            </div>
          </div>
        </div>
      ) : (
        <>
          <div className="mb-8 space-y-4">
            <p className="leading-relaxed text-muted-foreground">
              {email ? (
                <span>
                  We've sent a confirmation link to <br />
                  <strong className="font-semibold text-foreground">{email}</strong>
                </span>
              ) : (
                "Please check your inbox for a confirmation link."
              )}
            </p>
            <p className="inline-block rounded-xl border border-border-subtle bg-surface-subtle p-4 text-sm text-muted-foreground">
              Can't find it? Check your spam folder just in case.
            </p>
          </div>

          <div className="mx-auto flex max-w-xs flex-col gap-4">
            <button
              onClick={(e) => {
                e.preventDefault();
                onResendEmail();
              }}
              disabled={isResending}
              className="w-full rounded-xl bg-primary px-4 py-3.5 font-bold text-white shadow-sm transition-all duration-200 hover:-translate-y-0.5 hover:bg-primary-hover focus-visible:shadow-focus disabled:cursor-not-allowed disabled:opacity-70"
            >
              {isResending ? "Sending..." : "Resend Email"}
            </button>
            <button
              onClick={(e) => {
                e.preventDefault();
                redirectBackToLogin();
              }}
              className="py-2 text-sm font-semibold text-muted-foreground transition-colors hover:text-foreground"
            >
              Return to login
            </button>
          </div>

          <div className="mt-10 border-t border-border-subtle pt-8">
            <div className="inline-block">
              <PortalTooltip label="Confirming ensures you have permanent access to your account for password resets and updates.">
                <span className="flex cursor-help items-center justify-center text-xs font-semibold text-text-disabled transition-colors hover:text-muted-foreground">
                  <svg
                    className="mr-1.5 h-4 w-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    aria-hidden
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                  Why do I need to confirm?
                </span>
              </PortalTooltip>
            </div>
          </div>
        </>
      )}
    </div>
  );
};
